// Discriminated union mirroring the shapes returned by /api/v1/activity/quiz-mixed.
// `format` tells us how to render and how to score each question.

export type Format =
  | 'options'
  | 'true_false'
  | 'element_order'
  | 'fill_blank'
  | 'grouping'
  | 'matching'
  | 'open_ended';

export type FillBlankOption = { correct: string; available: string[] };
export type GroupingDef = { items: string[]; category: string };
export type MatchingDef = { item: string; match: string };

interface Base {
  question?: string;
  instruction?: string;
  explanation?: string;
}

export type MultipleChoiceQ = Base & {
  format: 'options';
  options: string[];
  correct_answer: number;
};
export type TrueFalseQ = Base & {
  format: 'true_false';
  correct_answer: boolean | number; // some backends serialise as 0/1
};
export type OrderingQ = Base & {
  format: 'element_order';
  elements: string[];
  correct_order: number[];
};
export type FillBlankQ = Base & {
  format: 'fill_blank';
  sentence: string;
  options: FillBlankOption[];
};
export type GroupingQ = Base & {
  format: 'grouping';
  groups: GroupingDef[];
};
export type MatchingQ = Base & {
  format: 'matching';
  pairs: MatchingDef[];
};
export type OpenEndedQ = Base & {
  format: 'open_ended';
  // Different backends call the model answer `correct_answer` or `answer`.
  // The view normalises both into `modelAnswer` before sending.
  correct_answer?: string;
  answer?: string;
};

export type Question =
  | MultipleChoiceQ
  | TrueFalseQ
  | OrderingQ
  | FillBlankQ
  | GroupingQ
  | MatchingQ
  | OpenEndedQ;

// Each format keeps its in-progress answer in a shape that lets us render
// partial state and compute correctness later.
export type Draft =
  | { format: 'options'; value: number | null }
  | { format: 'true_false'; value: boolean | null }
  // order[i] = index into the original elements array. The draft is always
  // a full permutation; the user reorders it with up/down buttons.
  | { format: 'element_order'; order: number[] }
  // blanks[i] = the picked option text (with suffix preserved), or null.
  | { format: 'fill_blank'; blanks: (string | null)[] }
  // assignments[itemIndex] = category name, or null when still in the pool.
  // The flat string[] of items is built once at draft init.
  | { format: 'grouping'; items: string[]; assignments: (string | null)[] }
  // matches[leftIndex] = rightIndex into the shuffled right column, or null.
  // The shuffled column orderings are kept on the draft for stable rendering.
  | {
      format: 'matching';
      leftOrder: number[]; // permutation of pair indices for the left column
      rightOrder: number[]; // permutation of pair indices for the right column
      matches: (number | null)[]; // for each LEFT slot (in leftOrder), the right slot picked
    }
  | { format: 'open_ended'; text: string };

// Default per-format hint shown above the answer body. Copy mirrors the
// strings used by Study (public/locales/es-ES/pages.json) so the two
// products stay consistent. The question's own `instruction` (if any) is
// still rendered separately by each question component.
export function hintFor(format: Format): string {
  switch (format) {
    case 'options':
      return 'Selecciona la opción correcta.';
    case 'true_false':
      return 'Indica si la afirmación es verdadera o falsa.';
    case 'element_order':
      return 'Usa las flechas para ordenar los elementos en la secuencia correcta.';
    case 'fill_blank':
      return 'Completa los espacios en blanco seleccionando las opciones correctas.';
    case 'grouping':
      return 'Categoriza cada elemento en el grupo que le corresponde.';
    case 'matching':
      return 'Empareja cada elemento con su correspondiente usando el menú desplegable.';
    case 'open_ended':
      return 'Escribe tu respuesta en el campo de texto.';
  }
}

// Stable random shuffle used to scramble pool ordering. The seed is the
// question position so the order is consistent across re-renders.
export function shuffled<T>(arr: T[], seed = 1): T[] {
  const a = arr.slice();
  let s = seed * 9301 + 49297;
  for (let i = a.length - 1; i > 0; i--) {
    s = (s * 9301 + 49297) % 233280;
    const j = Math.floor((s / 233280) * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

export function defaultDraft(q: Question, seed = 1): Draft {
  switch (q.format) {
    case 'options':
      return { format: 'options', value: null };
    case 'true_false':
      return { format: 'true_false', value: null };
    case 'element_order': {
      // Start the user with a shuffled permutation so they have to reorder.
      // If by chance the shuffle equals correct_order, the answer is just
      // already correct — that's fine.
      const idxs = Array.from({ length: q.elements.length }, (_, i) => i);
      return { format: 'element_order', order: shuffled(idxs, seed) };
    }
    case 'fill_blank':
      return {
        format: 'fill_blank',
        blanks: Array(q.options.length).fill(null)
      };
    case 'grouping': {
      const flat = q.groups.flatMap((g) => g.items);
      return {
        format: 'grouping',
        items: shuffled(flat, seed),
        assignments: Array(flat.length).fill(null)
      };
    }
    case 'matching': {
      const n = q.pairs.length;
      const idxs = Array.from({ length: n }, (_, i) => i);
      return {
        format: 'matching',
        leftOrder: shuffled(idxs, seed),
        rightOrder: shuffled(idxs, seed + 7),
        matches: Array(n).fill(null)
      };
    }
    case 'open_ended':
      return { format: 'open_ended', text: '' };
  }
}
